/**
 * Epic #228 P4 — the auto-save queue of one template draft, as a pure state
 * machine (design §5.1 with plan decisions PD3–PD9).
 *
 * `reduceDraftSaveQueue(state, event, newMutationId)` returns the next state
 * and a list of effects. Nothing here touches React, timers, `Date`,
 * Supabase or a service module at runtime: every effect is data for the
 * controller (`draft-save-queue-controller.ts`) to interpret. `newMutationId`
 * is the only injected dependency; it is called exactly when a NEW payload is
 * minted, never for a byte-identical resend (the server replays a known
 * `clientMutationId` without looking at the payload, so a reused id with
 * different values would silently drop edits).
 *
 * Invariants the tests pin:
 * - at most one request outstanding per queue; `send` always carries
 *   `expectedHeadRevisionId = base`, and `base` moves to the returned
 *   `draftRevisionId` on success (chained CAS);
 * - `base` moves only on this queue's own results, a rebase/reload fetch or
 *   `adoptHead` — never on a passive editor-state refetch (PD6);
 * - `baseValues[slot]` is the latest value this client confirmed (its own
 *   save) or observed (the value the dialog opened with) on the server for
 *   that slot; it is kept after a save and dropped for idle slots only when
 *   the editor state is observed again (code review B1);
 * - a failed payload is kept verbatim and resent first, unchanged (PD8);
 * - `pending` non-empty ⇒ a timer is armed, or the queue is paused, in
 *   conflict, or busy with a request/fetch;
 * - `failedPayload !== null ⇒ inFlightPayload === null`.
 */
import type { WebsiteTemplateServiceErrorCode } from '../../services/website-templates-errors';
import type { StoreWebsiteDocument, WebsiteSlotValue } from '../../types/website-templates';
import { websiteSlotValuesEqual, type WebsiteSlotBatch, type WebsiteSlotCommitMode } from './slots';

export type DraftSaveStatus =
  | 'idle'
  | 'dirty'
  | 'saving'
  | 'saved'
  | 'failed'
  | 'conflict'
  | 'paused';

export type DraftSaveCommitMode = WebsiteSlotCommitMode;

/** Typing settles for this long before a text edit is sent (PRD §11.2). */
export const DRAFT_SAVE_DEBOUNCE_MS = 1200;
/** Waits after `rate_limited`, in order; the next one fails (design §5.1). */
export const DRAFT_SAVE_BACKOFF_MS: readonly number[] = [2000, 5000];

/** One request: the values sent, the head they CAS on, and their idempotency key. */
export interface DraftSavePayload {
  readonly values: WebsiteSlotBatch;
  readonly expectedHeadRevisionId: string;
  readonly clientMutationId: string;
}

/** A value the server refused for one slot (PD7). Kept, shown, never re-sent as is. */
export interface DraftSaveRejection {
  readonly value: WebsiteSlotValue | null;
  readonly code: WebsiteTemplateServiceErrorCode;
}

/** A template head as another writer (open, locale, version update, reload) reports it. */
export interface DraftSaveHead {
  readonly revisionId: string;
  readonly slots?: Readonly<Record<string, WebsiteSlotValue>>;
}

/**
 * What the UI reads (`useSyncExternalStore`). The first block is the
 * interface fixed by the P4 plan; the second block is the overlay sources
 * (`draftSaveOverlay`) and the flags the chip and guard derive from.
 */
export interface DraftSaveSnapshot {
  readonly status: DraftSaveStatus;
  /** The head revision the next save CASes on. */
  readonly base: string;
  readonly pendingSlotIds: readonly string[];
  readonly inFlight: boolean;
  readonly hasFailedPayload: boolean;
  readonly rejected: Readonly<Record<string, DraftSaveRejection>>;
  /** The code of the last failure or rejection; null after a success or a discard. */
  readonly lastErrorCode: WebsiteTemplateServiceErrorCode | null;

  /** Edits waiting to be sent, latest value per slot. */
  readonly pending: WebsiteSlotBatch;
  readonly inFlightPayload: DraftSavePayload | null;
  readonly failedPayload: DraftSavePayload | null;
  /** Saved under `base`, kept until the editor state shows that head (PD5). */
  readonly lastSaved: WebsiteSlotBatch;
  /** What the merchant saw when a slot was first touched (PD5); the rebase base. */
  readonly baseValues: WebsiteSlotBatch;
  readonly paused: boolean;
  readonly conflict: boolean;
  readonly fetching: 'rebase' | 'reload' | null;
}

export interface DraftSaveQueueState extends DraftSaveSnapshot {
  /** The armed debounce timer, by kind. The controller holds the handle. */
  readonly timer: DraftSaveCommitMode | null;
  /** The strongest commit mode among the buffered edits (`immediate` wins). */
  readonly pendingMode: DraftSaveCommitMode | null;
  /** `discard()` ran while a request was out: its success still moves `base`, its failure is dropped. */
  readonly inFlightDiscarded: boolean;
  /** The in-flight payload was already resent once after a rebase: a second `stale_draft` is a conflict. */
  readonly rebaseRetried: boolean;
  /** Rate-limit waits already taken for the in-flight payload. */
  readonly backoff: number;
  /** A rate-limit wait is running: no request is out until `backoffElapsed`. */
  readonly backoffWaiting: boolean;
  /** An explicit `flush`/`retry`/`reload` is in progress: chain sends instead of re-arming. */
  readonly draining: boolean;
  /** What `status` is when there is nothing to do. */
  readonly restStatus: 'idle' | 'saved';
}

export type DraftSaveQueueEvent =
  | {
      readonly type: 'enqueue';
      readonly batch: WebsiteSlotBatch;
      readonly mode: DraftSaveCommitMode;
      /** Per slot, the value the merchant saw when the dialog opened (absent ≡ null). */
      readonly seen: WebsiteSlotBatch;
    }
  | { readonly type: 'timerFired' }
  | { readonly type: 'flushRequested' }
  | { readonly type: 'retry' }
  | { readonly type: 'reload' }
  | {
      readonly type: 'saveSucceeded';
      readonly clientMutationId: string;
      readonly draftRevisionId: string;
    }
  | {
      readonly type: 'saveFailed';
      readonly clientMutationId: string;
      readonly code: WebsiteTemplateServiceErrorCode;
      readonly slotId: string | null;
    }
  | { readonly type: 'headFetched'; readonly head: DraftSaveHead }
  | { readonly type: 'headFetchFailed'; readonly code: WebsiteTemplateServiceErrorCode }
  | { readonly type: 'backoffElapsed' }
  | { readonly type: 'pause' }
  | { readonly type: 'resume' }
  | { readonly type: 'adoptHead'; readonly head: DraftSaveHead }
  | { readonly type: 'discard' }
  /**
   * The editor state now shows this head: idle slots take their next rebase
   * base from what the merchant sees, and once it is `base` the last-saved
   * overlay is no longer needed.
   */
  | { readonly type: 'headObserved'; readonly revisionId: string };

export type DraftSaveEffect =
  | { readonly type: 'armTimer'; readonly ms: number }
  | { readonly type: 'cancelTimer' }
  | { readonly type: 'armBackoff'; readonly ms: number }
  | { readonly type: 'cancelBackoff' }
  | { readonly type: 'send'; readonly payload: DraftSavePayload }
  | { readonly type: 'fetchHead' }
  | {
      readonly type: 'saved';
      readonly draftRevisionId: string;
      readonly slotIds: readonly string[];
    }
  | {
      readonly type: 'slotRejected';
      readonly slotId: string;
      readonly code: WebsiteTemplateServiceErrorCode;
    }
  | { readonly type: 'log'; readonly code: string; readonly detail?: string };

export interface DraftSaveReduction {
  readonly state: DraftSaveQueueState;
  readonly effects: readonly DraftSaveEffect[];
}

/**
 * Codes that describe the request or the draft, not one value: they can
 * never isolate a slot (PD7). Everything else WITH a slot id is a
 * server-only rejection of that slot.
 */
const WHOLE_PAYLOAD_CODES: ReadonlySet<WebsiteTemplateServiceErrorCode> = new Set([
  'stale_draft',
  'rate_limited',
  'template_unavailable',
  'builder_retired',
  'permission_denied',
  'preview_expired',
  'network',
  'unknown',
]);

type Working = { -readonly [K in keyof DraftSaveQueueState]: DraftSaveQueueState[K] };

const hasOwn = (record: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

const isEmpty = (record: object): boolean => Object.keys(record).length === 0;

function strongest(a: DraftSaveCommitMode | null, b: DraftSaveCommitMode): DraftSaveCommitMode {
  return a === 'immediate' || b === 'immediate' ? 'immediate' : 'debounce';
}

function without(batch: WebsiteSlotBatch, slotId: string): WebsiteSlotBatch {
  const { [slotId]: _dropped, ...rest } = batch;
  return rest;
}

function deriveStatus(w: Working): DraftSaveStatus {
  if (w.paused) return 'paused';
  if (w.fetching === 'reload') return 'saving';
  if (w.conflict) return 'conflict';
  if (w.inFlightPayload !== null || w.fetching !== null) return 'saving';
  if (w.timer !== null) return 'dirty';
  if (w.failedPayload !== null) return 'failed';
  return w.restStatus;
}

function finalize(w: Working): DraftSaveQueueState {
  w.status = deriveStatus(w);
  w.pendingSlotIds = Object.keys(w.pending);
  w.inFlight = w.inFlightPayload !== null;
  w.hasFailedPayload = w.failedPayload !== null;
  return w;
}

export function initialDraftSaveQueueState(args: {
  readonly base: string;
  /** Accepted for the plan's call shape; first-touch values come from `enqueue.seen`. */
  readonly baseSlots?: Readonly<Record<string, WebsiteSlotValue>>;
}): DraftSaveQueueState {
  return finalize({
    status: 'idle',
    base: args.base,
    pendingSlotIds: [],
    inFlight: false,
    hasFailedPayload: false,
    rejected: {},
    lastErrorCode: null,
    pending: {},
    inFlightPayload: null,
    failedPayload: null,
    lastSaved: {},
    baseValues: {},
    paused: false,
    conflict: false,
    fetching: null,
    timer: null,
    pendingMode: null,
    inFlightDiscarded: false,
    rebaseRetried: false,
    backoff: 0,
    backoffWaiting: false,
    draining: false,
    restStatus: 'idle',
  });
}

function hasWork(w: DraftSaveQueueState): boolean {
  return !isEmpty(w.pending) || w.inFlightPayload !== null || w.failedPayload !== null;
}

function clearTimer(w: Working, effects: DraftSaveEffect[]): void {
  if (w.timer === null) return;
  w.timer = null;
  effects.push({ type: 'cancelTimer' });
}

function armTimer(w: Working, effects: DraftSaveEffect[], mode: DraftSaveCommitMode): void {
  w.timer = mode;
  w.pendingMode = mode;
  effects.push({ type: 'armTimer', ms: mode === 'immediate' ? 0 : DRAFT_SAVE_DEBOUNCE_MS });
}

/**
 * Sends the next payload: the failed one verbatim (same id), else the
 * pending edits under a NEW id. Precondition: nothing outstanding.
 */
function startSend(w: Working, effects: DraftSaveEffect[], newMutationId: () => string): void {
  clearTimer(w, effects);
  if (w.failedPayload !== null) {
    w.inFlightPayload = { ...w.failedPayload, expectedHeadRevisionId: w.base };
    w.failedPayload = null;
  } else if (!isEmpty(w.pending)) {
    w.inFlightPayload = {
      values: w.pending,
      expectedHeadRevisionId: w.base,
      clientMutationId: newMutationId(),
    };
    w.pending = {};
    w.pendingMode = null;
  } else {
    w.draining = false;
    return;
  }
  w.inFlightDiscarded = false;
  w.lastErrorCode = null;
  effects.push({ type: 'send', payload: w.inFlightPayload });
}

function settleRequest(w: Working): DraftSavePayload {
  const sent = w.inFlightPayload as DraftSavePayload;
  w.inFlightPayload = null;
  w.inFlightDiscarded = false;
  w.backoff = 0;
  w.backoffWaiting = false;
  w.rebaseRetried = false;
  w.fetching = null;
  return sent;
}

function toFailed(
  w: Working,
  effects: DraftSaveEffect[],
  sent: DraftSavePayload,
  code: WebsiteTemplateServiceErrorCode,
): void {
  w.failedPayload = sent;
  w.lastErrorCode = code;
  w.draining = false;
  effects.push({ type: 'log', code });
}

function toConflict(w: Working, effects: DraftSaveEffect[], sent: DraftSavePayload): void {
  // The merchant's values are kept: the sent ones under the newer pending ones.
  w.pending = { ...sent.values, ...w.pending };
  w.pendingMode = w.pendingMode ?? 'immediate';
  w.conflict = true;
  w.lastErrorCode = 'stale_draft';
  w.draining = false;
  clearTimer(w, effects);
  effects.push({ type: 'log', code: 'stale_draft', detail: 'conflict' });
}

/** After a success or a rejection: what happens to the edits still pending. */
function continueWithPending(
  w: Working,
  effects: DraftSaveEffect[],
  newMutationId: () => string,
): void {
  if (isEmpty(w.pending) && w.failedPayload === null) {
    w.draining = false;
    return;
  }
  if (w.draining) {
    startSend(w, effects, newMutationId);
    return;
  }
  if (w.paused || w.conflict) return;
  if (!isEmpty(w.pending)) armTimer(w, effects, w.pendingMode ?? 'debounce');
}

export function reduceDraftSaveQueue(
  state: DraftSaveQueueState,
  event: DraftSaveQueueEvent,
  newMutationId: () => string,
): DraftSaveReduction {
  const effects: DraftSaveEffect[] = [];
  const w: Working = { ...state };
  const unchanged = { state, effects };

  switch (event.type) {
    case 'enqueue': {
      const slotIds = Object.keys(event.batch);
      if (slotIds.length === 0) return unchanged;
      const pending = { ...w.pending };
      const baseValues = { ...w.baseValues };
      const rejected = { ...w.rejected };
      for (const slotId of slotIds) {
        pending[slotId] = event.batch[slotId] ?? null;
        if (!hasOwn(baseValues, slotId)) {
          baseValues[slotId] = hasOwn(event.seen, slotId) ? (event.seen[slotId] ?? null) : null;
        }
        delete rejected[slotId];
      }
      w.pending = pending;
      w.baseValues = baseValues;
      w.rejected = rejected;
      const busy = w.inFlightPayload !== null || w.fetching !== null;
      if (w.conflict || w.paused || busy) {
        w.pendingMode = strongest(w.pendingMode, event.mode);
        return { state: finalize(w), effects };
      }
      // An armed immediate timer fires at once and carries the debounced
      // slots with it; a debounce edit restarts the debounce.
      if (w.timer === 'immediate') {
        w.pendingMode = 'immediate';
      } else {
        armTimer(w, effects, strongest(w.pendingMode, event.mode));
      }
      return { state: finalize(w), effects };
    }

    case 'timerFired': {
      if (w.timer === null) return unchanged;
      w.timer = null;
      if (w.paused || w.conflict || w.inFlightPayload !== null || w.fetching !== null) {
        return { state: finalize(w), effects };
      }
      startSend(w, effects, newMutationId);
      return { state: finalize(w), effects };
    }

    case 'flushRequested':
    case 'retry': {
      // A conflict is resolved by `reload` only; nothing to send resolves at once.
      if (w.conflict || !hasWork(w)) return unchanged;
      w.draining = true;
      if (w.inFlightPayload === null && w.fetching === null) startSend(w, effects, newMutationId);
      return { state: finalize(w), effects };
    }

    case 'reload': {
      if (w.fetching !== null || w.inFlightPayload !== null) return unchanged;
      w.fetching = 'reload';
      w.draining = true;
      clearTimer(w, effects);
      effects.push({ type: 'fetchHead' });
      return { state: finalize(w), effects };
    }

    case 'saveSucceeded': {
      const sent = w.inFlightPayload;
      if (sent === null || sent.clientMutationId !== event.clientMutationId) return unchanged;
      const discarded = w.inFlightDiscarded;
      settleRequest(w);
      w.base = event.draftRevisionId;
      w.lastErrorCode = null;
      w.restStatus = 'saved';
      const slotIds = Object.keys(sent.values);
      const lastSaved = { ...w.lastSaved };
      const baseValues = { ...w.baseValues };
      for (const slotId of slotIds) {
        const value = sent.values[slotId] ?? null;
        lastSaved[slotId] = value;
        // The server now holds this value: it is the rebase base of the next
        // edit of the slot, until the editor state shows a newer head.
        baseValues[slotId] = value;
      }
      w.lastSaved = lastSaved;
      if (!discarded) w.baseValues = baseValues;
      effects.push({ type: 'saved', draftRevisionId: event.draftRevisionId, slotIds });
      continueWithPending(w, effects, newMutationId);
      return { state: finalize(w), effects };
    }

    case 'saveFailed': {
      const sent = w.inFlightPayload;
      if (sent === null || sent.clientMutationId !== event.clientMutationId) return unchanged;
      if (w.inFlightDiscarded) {
        // The merchant left without saving: the lost request is not parked.
        settleRequest(w);
        w.draining = false;
        effects.push({ type: 'log', code: event.code, detail: 'discarded' });
        continueWithPending(w, effects, newMutationId);
        return { state: finalize(w), effects };
      }
      if (event.code === 'stale_draft') {
        if (!w.rebaseRetried) {
          w.fetching = 'rebase';
          effects.push({ type: 'fetchHead' });
          return { state: finalize(w), effects };
        }
        settleRequest(w);
        toConflict(w, effects, sent);
        return { state: finalize(w), effects };
      }
      if (event.code === 'rate_limited') {
        const wait = DRAFT_SAVE_BACKOFF_MS[w.backoff];
        if (wait !== undefined) {
          w.backoff += 1;
          w.backoffWaiting = true;
          effects.push({ type: 'armBackoff', ms: wait });
          return { state: finalize(w), effects };
        }
        settleRequest(w);
        toFailed(w, effects, sent, event.code);
        return { state: finalize(w), effects };
      }
      const slotId = event.slotId;
      if (slotId !== null && !WHOLE_PAYLOAD_CODES.has(event.code) && hasOwn(sent.values, slotId)) {
        settleRequest(w);
        w.lastErrorCode = event.code;
        // A newer pending value for that slot is tried instead of being rejected.
        if (!hasOwn(w.pending, slotId)) {
          w.rejected = {
            ...w.rejected,
            [slotId]: { value: sent.values[slotId] ?? null, code: event.code },
          };
          effects.push({ type: 'slotRejected', slotId, code: event.code });
        }
        effects.push({ type: 'log', code: event.code, detail: 'slot' });
        // The RPC is atomic: nothing of the payload persisted. The survivors
        // go first, newer pending edits win, and they leave under a NEW id.
        w.pending = { ...without(sent.values, slotId), ...w.pending };
        w.pendingMode = w.pendingMode ?? 'immediate';
        if (isEmpty(w.pending)) {
          w.draining = false;
        } else if (w.paused && !w.draining) {
          // Buffered; `resume` re-arms.
        } else {
          startSend(w, effects, newMutationId);
        }
        return { state: finalize(w), effects };
      }
      settleRequest(w);
      toFailed(w, effects, sent, event.code);
      return { state: finalize(w), effects };
    }

    case 'headFetched': {
      if (w.fetching === null) return unchanged;
      const remote = event.head.slots ?? {};
      const remoteValue = (slotId: string): WebsiteSlotValue | null =>
        hasOwn(remote, slotId) ? (remote[slotId] ?? null) : null;
      if (w.fetching === 'rebase') {
        const sent = w.inFlightPayload as DraftSavePayload;
        if (w.inFlightDiscarded) {
          settleRequest(w);
          w.base = event.head.revisionId;
          w.lastSaved = {};
          w.draining = false;
          continueWithPending(w, effects, newMutationId);
          return { state: finalize(w), effects };
        }
        const touched = [...Object.keys(sent.values), ...Object.keys(w.pending)];
        const untouchedRemotely = touched.every((slotId) =>
          websiteSlotValuesEqual(
            remoteValue(slotId),
            hasOwn(w.baseValues, slotId) ? w.baseValues[slotId] : null,
          ),
        );
        w.fetching = null;
        if (!untouchedRemotely) {
          settleRequest(w);
          toConflict(w, effects, sent);
          return { state: finalize(w), effects };
        }
        // Rebase: same values, same id, the newer head. Nothing was recorded
        // under the id (the CAS raised before any insert), so a replay is safe.
        w.base = event.head.revisionId;
        w.lastSaved = {};
        w.rebaseRetried = true;
        w.inFlightPayload = { ...sent, expectedHeadRevisionId: w.base };
        effects.push({ type: 'send', payload: w.inFlightPayload });
        return { state: finalize(w), effects };
      }
      // Reload (PD9): the merchant's values go on top of the newer draft.
      w.fetching = null;
      w.base = event.head.revisionId;
      w.lastSaved = {};
      w.conflict = false;
      w.lastErrorCode = null;
      w.rebaseRetried = false;
      const baseValues = { ...w.baseValues };
      for (const slotId of [
        ...Object.keys(w.pending),
        ...Object.keys(w.failedPayload?.values ?? {}),
      ]) {
        baseValues[slotId] = remoteValue(slotId);
      }
      w.baseValues = baseValues;
      if (hasWork(w)) startSend(w, effects, newMutationId);
      else w.draining = false;
      return { state: finalize(w), effects };
    }

    case 'headFetchFailed': {
      if (w.fetching === null) return unchanged;
      if (w.fetching === 'rebase') {
        const discarded = w.inFlightDiscarded;
        const sent = settleRequest(w);
        if (discarded) {
          w.draining = false;
          continueWithPending(w, effects, newMutationId);
          return { state: finalize(w), effects };
        }
        toFailed(w, effects, sent, event.code);
        return { state: finalize(w), effects };
      }
      w.fetching = null;
      w.lastErrorCode = event.code;
      w.draining = false;
      effects.push({ type: 'log', code: event.code, detail: 'reload' });
      return { state: finalize(w), effects };
    }

    case 'backoffElapsed': {
      if (w.inFlightPayload === null || !w.backoffWaiting || w.fetching !== null) return unchanged;
      // The same request, continued: it ignores `paused`.
      w.backoffWaiting = false;
      effects.push({ type: 'send', payload: w.inFlightPayload });
      return { state: finalize(w), effects };
    }

    case 'pause': {
      if (w.paused) return unchanged;
      w.paused = true;
      clearTimer(w, effects);
      return { state: finalize(w), effects };
    }

    case 'resume': {
      if (!w.paused) return unchanged;
      w.paused = false;
      const busy = w.inFlightPayload !== null || w.fetching !== null;
      if (!busy && !w.conflict && !isEmpty(w.pending)) {
        armTimer(w, effects, w.pendingMode ?? 'debounce');
      }
      return { state: finalize(w), effects };
    }

    case 'adoptHead': {
      if (hasWork(w) || w.fetching !== null) {
        effects.push({ type: 'log', code: 'adopt_head_refused' });
        return { state, effects };
      }
      w.base = event.head.revisionId;
      w.lastSaved = {};
      w.baseValues = {};
      w.conflict = false;
      w.lastErrorCode = null;
      w.rebaseRetried = false;
      return { state: finalize(w), effects };
    }

    case 'discard': {
      w.pending = {};
      w.pendingMode = null;
      w.failedPayload = null;
      w.rejected = {};
      w.baseValues = {};
      w.conflict = false;
      w.lastErrorCode = null;
      w.draining = false;
      w.rebaseRetried = false;
      clearTimer(w, effects);
      if (w.inFlightPayload !== null) {
        if (w.backoffWaiting) {
          // Waiting out a rate limit: no request is out, so nothing is resent.
          w.inFlightPayload = null;
          w.backoff = 0;
          w.backoffWaiting = false;
          effects.push({ type: 'cancelBackoff' });
        } else {
          // A request (or a backoff resend) is out: its result still settles it.
          w.inFlightDiscarded = true;
        }
      }
      return { state: finalize(w), effects };
    }

    case 'headObserved': {
      // The editor state is what the merchant sees now: a slot with no work
      // takes its next rebase base from the next `enqueue`'s `seen`.
      const busySlots = new Set([
        ...Object.keys(w.pending),
        ...Object.keys(w.inFlightPayload?.values ?? {}),
        ...Object.keys(w.failedPayload?.values ?? {}),
      ]);
      const idle = Object.keys(w.baseValues).filter((slotId) => !busySlots.has(slotId));
      const caughtUp = event.revisionId === w.base && !isEmpty(w.lastSaved);
      if (idle.length === 0 && !caughtUp) return unchanged;
      if (idle.length > 0) {
        const baseValues = { ...w.baseValues };
        for (const slotId of idle) delete baseValues[slotId];
        w.baseValues = baseValues;
      }
      if (caughtUp) w.lastSaved = {};
      return { state: finalize(w), effects };
    }
  }
}

// ---------------------------------------------------------------------------
// Selectors

export type DraftSaveIndicator = 'saving' | 'saved' | 'failed' | 'rejected' | 'conflict';

/** Edits not yet confirmed by the server: pending, in flight or parked after a failure. */
export function draftSaveHasWork(s: DraftSaveSnapshot): boolean {
  return s.pendingSlotIds.length > 0 || s.inFlight || s.hasFailedPayload;
}

/**
 * What the status chip shows (PD12). Never `saved` while a timer is armed,
 * a request is unanswered or a payload is parked (PRD §11.3).
 */
export function draftSaveIndicator(s: DraftSaveSnapshot): DraftSaveIndicator {
  if (s.status === 'conflict') return 'conflict';
  if (s.status === 'failed') return 'failed';
  if (draftSaveHasWork(s) || s.fetching !== null) return 'saving';
  if (!isEmpty(s.rejected)) return 'rejected';
  return 'saved';
}

/** PD11: leaving needs a flush or a question. Saved-but-unpublished is not armed. */
export function draftSaveNeedsLeaveGuard(s: DraftSaveSnapshot): boolean {
  return (
    draftSaveHasWork(s) || s.status === 'failed' || s.status === 'conflict' || !isEmpty(s.rejected)
  );
}

/**
 * The document as the server confirmed it to this client: the loaded slots
 * under the values this queue saved (until the editor state caught up). The
 * dialog reads a slot's `seen` (its rebase base on first touch) from here,
 * never from a pending, parked or rejected value, which the server does not
 * hold (code review B1).
 */
export function draftSaveObservedSlots(
  s: DraftSaveSnapshot,
  slots: StoreWebsiteDocument['slots'],
): StoreWebsiteDocument['slots'] {
  const result: Record<string, WebsiteSlotValue> = { ...slots };
  for (const slotId of Object.keys(s.lastSaved)) {
    const value = s.lastSaved[slotId];
    if (value === null || value === undefined) delete result[slotId];
    else result[slotId] = value;
  }
  return result;
}

/**
 * PD5: the loaded document overlaid with what the queue knows is newer —
 * last-saved values (until the editor state shows `base`), kept rejections,
 * the parked payload, the in-flight payload and the pending edits, in that
 * order. A `null` layer value removes the slot (template default again).
 */
export function draftSaveOverlay(
  s: DraftSaveSnapshot,
  slots: StoreWebsiteDocument['slots'],
): StoreWebsiteDocument['slots'] {
  const rejectedValues: Record<string, WebsiteSlotValue | null> = {};
  for (const slotId of Object.keys(s.rejected)) {
    rejectedValues[slotId] = s.rejected[slotId]?.value ?? null;
  }
  const layers: readonly WebsiteSlotBatch[] = [
    s.lastSaved,
    rejectedValues,
    s.failedPayload?.values ?? {},
    s.inFlightPayload?.values ?? {},
    s.pending,
  ];
  const result: Record<string, WebsiteSlotValue> = { ...slots };
  for (const layer of layers) {
    for (const slotId of Object.keys(layer)) {
      const value = layer[slotId];
      if (value === null || value === undefined) delete result[slotId];
      else result[slotId] = value;
    }
  }
  return result;
}
